/**
 * LinkPreview — exibe miniatura/card de preview para URLs em mensagens de chat.
 *
 * Busca metadados OG via edge function `fetch-link-preview` para evitar CORS.
 * Mostra: imagem, título, descrição e domínio.
 */

import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PreviewData {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  siteName?: string | null;
  url: string;
}

interface LinkPreviewProps {
  url: string;
  isUserMessage?: boolean;
}

// Cache em memória para não re-buscar a mesma URL na sessão
const previewCache = new Map<string, PreviewData | null>();

export const LinkPreview = ({ url, isUserMessage }: LinkPreviewProps) => {
  const [data, setData] = useState<PreviewData | null | undefined>(undefined); // undefined = carregando

  useEffect(() => {
    let cancelled = false;

    const fetchPreview = async () => {
      if (previewCache.has(url)) {
        setData(previewCache.get(url) ?? null);
        return;
      }

      try {
        const { data: result, error } = await supabase.functions.invoke('fetch-link-preview', {
          body: { url },
        });

        if (cancelled) return;

        if (error || !result) {
          previewCache.set(url, null);
          setData(null);
          return;
        }

        const preview: PreviewData = result;
        previewCache.set(url, preview);
        setData(preview);
      } catch {
        if (!cancelled) {
          previewCache.set(url, null);
          setData(null);
        }
      }
    };

    fetchPreview();
    return () => { cancelled = true; };
  }, [url]);

  // Carregando
  if (data === undefined) {
    return (
      <div
        className={`mt-2 rounded-xl overflow-hidden border animate-pulse ${
          isUserMessage
            ? 'border-border bg-card/60'
            : 'border-border bg-secondary'
        }`}
      >
        <div className="h-20 bg-input/30" />
        <div className="p-2.5 space-y-1.5">
          <div className="h-3 bg-input/50 rounded w-3/4" />
          <div className="h-2 bg-input/30 rounded w-1/2" />
        </div>
      </div>
    );
  }

  // Sem dados úteis
  if (!data || (!data.title && !data.image)) return null;

  let hostname = url;
  try { hostname = new URL(url).hostname; } catch { /* noop */ }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-2 no-underline"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`rounded-xl overflow-hidden border transition-opacity hover:opacity-90 ${
          isUserMessage
            ? 'border-border bg-card/70'
            : 'border-border bg-muted'
        }`}
      >
        {data.image && (
          <img
            src={data.image}
            alt={data.title || ''}
            className="w-full h-32 object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div className="px-3 py-2">
          {data.title && (
            <p
              className={`text-xs font-semibold leading-tight line-clamp-2 ${
                isUserMessage ? 'text-white' : 'text-foreground'
              }`}
            >
              {data.title}
            </p>
          )}
          {data.description && (
            <p
              className={`text-[11px] mt-0.5 line-clamp-2 ${
                isUserMessage ? 'text-info-text' : 'text-muted-foreground'
              }`}
            >
              {data.description}
            </p>
          )}
          <div
            className={`flex items-center gap-1 mt-1 ${
              isUserMessage ? 'text-info-text' : 'text-muted-foreground'
            }`}
          >
            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
            <p className="text-[10px] truncate">{data.siteName || hostname}</p>
          </div>
        </div>
      </div>
    </a>
  );
};
